import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Search, MapPin, Users, Loader2 } from "lucide-react";
import { toast } from "sonner";
import AppShell from "@/components/AppShell";
import { useAuth, ROLE_THEMES } from "@/context/AuthContext";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CATEGORY_META, STATUS_META } from "@/lib/communityMeta";

const CATS = Object.entries(CATEGORY_META);

export default function CommunityIssues() {
  const { user } = useAuth();
  const nav = useNavigate();
  const theme = user ? ROLE_THEMES[user.role] : ROLE_THEMES.farmer;
  const allowed = user && ["farmer", "day_worker"].includes(user.role);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [status, setStatus] = useState("all");
  const [flags, setFlags] = useState({ is_admin: false, is_rep: false });
  const [myRequest, setMyRequest] = useState(null);
  const [showResolver, setShowResolver] = useState(false);
  const [resolverForm, setResolverForm] = useState({ service_areas: "", reason: "" });

  useEffect(() => {
    if (!allowed) return;
    api.get("/me/community-flags").then((r) => setFlags(r.data)).catch(() => {});
    api.get("/resolver-requests/mine").then((r) => setMyRequest(r.data || null)).catch(() => {});
  }, [allowed]);

  const submitResolverRequest = async () => {
    if (!resolverForm.service_areas.trim()) return toast.error("Enter at least one village / area");
    try {
      await api.post("/resolver-requests", resolverForm);
      toast.success("Request submitted — GTF Admin will review");
      setShowResolver(false);
      const r = await api.get("/resolver-requests/mine"); setMyRequest(r.data || null);
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  useEffect(() => {
    if (!allowed) return;
    setLoading(true);
    api.get("/issues", { params: {
      q: q || undefined, category: cat !== "all" ? cat : undefined,
      status: status !== "all" ? status : undefined,
    } }).then((r) => setItems(r.data))
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  }, [q, cat, status, allowed]);

  if (!allowed) {
    return (
      <AppShell>
        <div className="px-4 md:px-8 lg:px-12 py-24 text-center">
          <div className="text-5xl mb-4">🌱</div>
          <h1 className="text-2xl font-bold text-gray-900">Community Issues</h1>
          <p className="text-gray-600 mt-2 max-w-md mx-auto">
            This feature is available only on the Farmer or Day Worker workspace.
            Switch workspace from the top bar to continue.
          </p>
        </div>
      </AppShell>
    );
  }

  return (
    <AppShell>
      <div className="px-4 md:px-8 lg:px-12 py-8">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="text-xs uppercase tracking-[0.24em] text-gray-500">Community</div>
            <h1 className="text-3xl md:text-4xl font-bold text-gray-900 tracking-tight">🌱 Community Issues</h1>
            <p className="text-gray-600 mt-1">See a local problem? Report it and make it visible.</p>
            <p className="text-[11px] text-gray-500 mt-2 italic max-w-2xl">
              Grow Together Farmily provides a platform to report, share and coordinate visibility of local community issues. Resolution depends on the concerned people, authorities or organizations.
            </p>
          </div>
          <button data-testid="community-report-btn" onClick={() => nav("/community/report")}
            className={`inline-flex items-center gap-2 px-5 py-3 rounded-full ${theme.bg} text-white font-semibold premium-shadow`}>
            <Plus className="h-4 w-4" /> Report Local Issue
          </button>
        </div>

        {!flags.is_rep && (
          <div className="mt-5 rounded-2xl bg-indigo-50 border border-indigo-200 p-4 text-sm flex flex-wrap items-center justify-between gap-3">
            <div>
              <div className="font-semibold text-indigo-900">Help your village — become a GTF Community Representative</div>
              <div className="text-xs text-indigo-800 mt-0.5">
                {myRequest?.status === "pending" ? "Your request is pending Admin review." :
                 myRequest?.status === "rejected" ? "Your previous request was rejected. You may re-apply with better details." :
                 myRequest?.status === "suspended" ? "Your resolver access was suspended. Contact GTF Admin." :
                 "Approved resolvers coordinate follow-up on reported issues in their village. Admin approval is required."}
              </div>
            </div>
            <button data-testid="community-apply-rep" onClick={() => setShowResolver(true)}
              disabled={myRequest?.status === "pending"}
              className="px-4 py-2 rounded-full bg-indigo-600 text-white font-semibold text-xs disabled:opacity-50">
              {myRequest?.status === "pending" ? "Request pending" : "Apply to be a Representative"}
            </button>
          </div>
        )}

        <div className="mt-6 flex gap-3 flex-wrap">
          <div className="flex-1 min-w-[240px] flex items-center gap-2 bg-white rounded-full border border-gray-200 px-5 py-3 premium-shadow">
            <Search className="h-4 w-4 text-gray-400" />
            <input data-testid="community-search" value={q} onChange={(e) => setQ(e.target.value)}
              placeholder="Search by title, village or description…"
              className="flex-1 outline-none bg-transparent text-sm" />
          </div>
          <select data-testid="community-filter-cat" value={cat} onChange={(e) => setCat(e.target.value)}
            className="bg-white rounded-full border border-gray-200 px-4 py-3 text-sm">
            <option value="all">All categories</option>
            {CATS.map(([k, v]) => <option key={k} value={k}>{v.icon} {v.label}</option>)}
          </select>
          <select data-testid="community-filter-status" value={status} onChange={(e) => setStatus(e.target.value)}
            className="bg-white rounded-full border border-gray-200 px-4 py-3 text-sm">
            <option value="all">All status</option>
            {Object.entries(STATUS_META).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
          </select>
        </div>

        {loading ? (
          <div className="mt-16 flex justify-center text-gray-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : items.length === 0 ? (
          <div className="mt-10 rounded-3xl bg-white border border-gray-100 p-16 text-center premium-shadow">
            <div className="text-5xl">🌱</div>
            <div className="mt-3 text-xl font-semibold text-gray-900">No issues yet</div>
            <div className="text-sm text-gray-500 mt-1 max-w-md mx-auto">
              A local problem should not remain unseen. Be the first to report an issue in your area.
            </div>
            <button data-testid="community-empty-report" onClick={() => nav("/community/report")}
              className={`mt-6 inline-flex items-center gap-2 px-5 py-3 rounded-full ${theme.bg} text-white font-semibold`}>
              <Plus className="h-4 w-4" /> Report Issue
            </button>
          </div>
        ) : (
          <div className="mt-6 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
            {items.map((it) => {
              const meta = CATEGORY_META[it.category] || CATEGORY_META.other;
              const sm = STATUS_META[it.status] || STATUS_META.reported;
              return (
                <button key={it.id} data-testid={`issue-card-${it.id}`} onClick={() => nav(`/community/${it.id}`)}
                  className="text-left rounded-3xl bg-white border border-gray-100 p-6 premium-shadow hover:-translate-y-1 transition-all">
                  <div className="flex items-start justify-between gap-3">
                    <div className="text-3xl">{meta.icon}</div>
                    <span className={`text-[10px] uppercase tracking-widest font-semibold px-3 py-1 rounded-full border ${sm.color}`}>{sm.label}</span>
                  </div>
                  <div className="mt-3 font-semibold text-gray-900 line-clamp-2">{it.title}</div>
                  <div className="text-xs text-gray-500 mt-1 uppercase tracking-wider">{meta.label}</div>
                  <div className="mt-4 flex items-center gap-4 text-xs text-gray-500">
                    <span className="flex items-center gap-1"><MapPin className="h-3.5 w-3.5" />{it.village}</span>
                    <span className="flex items-center gap-1"><Users className="h-3.5 w-3.5" />{it.affected_count || 1} affected</span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>

      {showResolver && (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowResolver(false)}>
          <div className="bg-white rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-bold text-gray-900">Apply to be a GTF Community Representative</div>
            <p className="text-xs text-gray-500 mt-1">
              GTF Admin will review your request. Representatives help coordinate follow-up — they are NOT government officials.
            </p>
            <label className="mt-3 block text-xs font-semibold text-gray-700">Villages / areas you can cover (comma-separated)</label>
            <input data-testid="resolver-areas" value={resolverForm.service_areas}
              onChange={(e) => setResolverForm({ ...resolverForm, service_areas: e.target.value })}
              placeholder="e.g. Daroji, Kampli, Hospet"
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <label className="mt-3 block text-xs font-semibold text-gray-700">Why are you a good fit? (optional)</label>
            <textarea rows={3} value={resolverForm.reason}
              onChange={(e) => setResolverForm({ ...resolverForm, reason: e.target.value })}
              placeholder="Share your village ties, past community work etc."
              className="mt-1 w-full rounded-xl border border-gray-200 px-4 py-3 text-sm" />
            <div className="mt-4 flex gap-2 justify-end">
              <button onClick={() => setShowResolver(false)} className="px-4 py-2 rounded-full border border-gray-200">Cancel</button>
              <button data-testid="resolver-apply-save" onClick={submitResolverRequest}
                className="px-4 py-2 rounded-full bg-indigo-600 text-white font-semibold">Submit</button>
            </div>
          </div>
        </div>
      )}
    </AppShell>
  );
}
